import React, { useMemo } from 'react';
import { useLabDateBuckets } from '../../../../../hooks/api/useLabDateBuckets';
import * as Card from '../../../../v2BaseComponents/Card';
import getDateString from '../../../../../utils/getDateString';
import { getIntl } from '../../../../../utils/intl';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useDateLabs } from '../../../../../hooks/api/useDateLabs';
import SkeletonLoadingContainer from '../../../../v2BaseComponents/SkeletonLoadingContainer';
import { dedupeLabs, getLabValueName } from '../../../../../utils/medicalDataUtils';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import IsInViewport from '../../../../v2BaseComponents/IsInViewport';
import _truncate from 'lodash/truncate';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';

const intl = getIntl('lab-date-bucket-list', {
    lab: '1 lab',
    labs: '{count} labs',
});

export type LabDateBucketListOwnProps = {
    medicalProfileId: string;
};

export type LabDateBucketListProps = LabDateBucketListOwnProps;

const DateBucketCard: React.FC<{ date: string; medicalProfileId: string; isInViewport: boolean }> = ({
    date,
    medicalProfileId,
    isInViewport,
}) => {
    const { navigate } = useProfilesTabNavigation();
    const { isLoadingDateLabs, dateLabs } = useDateLabs({
        medicalProfileId,
        date,
        isDisabled: !isInViewport,
    });

    const dedupedDateLabs = useMemo(() => {
        const allLabValues: MedicalDataTypes.PrimaryRecordLab[] = [];
        dateLabs.panels.forEach((panel) => {
            allLabValues.push(...panel.labs);
        });
        allLabValues.push(...dateLabs.unmatchedLabs);
        const deduped = dedupeLabs(allLabValues);
        return deduped;
    }, [dateLabs]);

    const description = useMemo(() => {
        if (dedupedDateLabs.length === 0 || isLoadingDateLabs || !isInViewport)
            return new Array(50).fill('a').join(' ');

        const names = dedupedDateLabs.flatMap((lab) => lab.values.map((value) => getLabValueName(lab, value)));
        const uniqueNames = Array.from(new Set(names));
        const returnStringParts: string[] = [];
        for (let i = 0; i < uniqueNames.length; i++) {
            const maybeNewReturnValue = returnStringParts.join(', ') + ', ' + uniqueNames[i];
            if (maybeNewReturnValue.length < 200) {
                returnStringParts.push(uniqueNames[i]);
                continue;
            }
        }

        if (returnStringParts.length < uniqueNames.length) {
            returnStringParts.push('...');
        }

        return returnStringParts.join(', ');
    }, [dedupedDateLabs, isLoadingDateLabs, isInViewport]);

    return (
        <Card.Card
            onPress={() => {
                navigate('date-labs', {
                    profileId: medicalProfileId,
                    date: date,
                });
            }}
            testID="date-bucket-card"
        >
            <Card.CardContent>
                <Card.CardInlineContent>
                    <Card.CardHeader>{getDateString(date, 'date')}</Card.CardHeader>
                    <SkeletonLoadingContainer isLoading={isLoadingDateLabs || !isInViewport}>
                        <Card.CardChip
                            title={
                                dedupedDateLabs.length === 1
                                    ? intl('lab')
                                    : intl('labs', { count: dedupedDateLabs.length })
                            }
                        />
                    </SkeletonLoadingContainer>
                </Card.CardInlineContent>
                <SkeletonLoadingContainer isLoading={isLoadingDateLabs || !isInViewport}>
                    <Card.CardBody2>{description}</Card.CardBody2>
                </SkeletonLoadingContainer>
            </Card.CardContent>
        </Card.Card>
    );
};

const LabDateBucketList: React.FC<LabDateBucketListProps> = ({ medicalProfileId }) => {
    const { isLoadingLabDateBuckets, labDateBuckets } = useLabDateBuckets({ medicalProfileId });
    const sortedLabDateBuckets = labDateBuckets.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <AnimateLoadedContent isLoading={isLoadingLabDateBuckets}>
            {() => (
                <ActionButtonLayout scrollable>
                    <MainContentContainer>
                        <ContentStack>
                            {sortedLabDateBuckets.map((bucket) => (
                                <IsInViewport key={bucket.date} style={{ width: '100%' }}>
                                    {({ isVisible }) => (
                                        <DateBucketCard
                                            date={bucket.date}
                                            medicalProfileId={medicalProfileId}
                                            isInViewport={isVisible}
                                        />
                                    )}
                                </IsInViewport>
                            ))}
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(LabDateBucketList);
